import React from "react";
import { useInView } from "../hooks/useInView";

type FadeProps<E extends keyof JSX.IntrinsicElements> = {
  as?: E;
  className?: string;
  children: React.ReactNode;
} & Omit<JSX.IntrinsicElements[E], "ref" | "className" | "children">;

/** Fades + slides content up once it scrolls into view (`.fade` → `.fade.in`). */
export function Fade<E extends keyof JSX.IntrinsicElements = "div">({
  as,
  className = "",
  children,
  ...rest
}: FadeProps<E>) {
  const Tag = (as || "div") as keyof JSX.IntrinsicElements;
  const [ref, inView] = useInView<HTMLElement>();
  return React.createElement(
    Tag,
    { ref, className: `fade${inView ? " in" : ""}${className ? ` ${className}` : ""}`, ...rest },
    children
  );
}

type LMaskProps = {
  className?: string;
  children: React.ReactNode;
};

/**
 * Clip-mask reveal: inner content slides up into place. Descender room (for the
 * italic "j" in the hero headline) is reserved in CSS so the mask never needs to
 * flip overflow after the animation.
 */
export function LMask({ className = "", children }: LMaskProps) {
  const [ref, inView] = useInView<HTMLSpanElement>();

  return (
    <span
      ref={ref}
      className={`lmask${inView ? " in" : ""}${className ? ` ${className}` : ""}`}
    >
      <span>{children}</span>
    </span>
  );
}
